import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import SiteLayout from './components/SiteLayout';
import Home from './components/Home';

const Success = lazy(() => import('./components/Success'));
const PaymentFailed = lazy(() => import('./components/PaymentFailed'));
const Downloads = lazy(() => import('./components/Downloads'));
const Documentation = lazy(() => import('./components/Documentation'));
const DocsSectionPage = lazy(() => import('./components/DocsSectionPage'));
const SobreCerty = lazy(() => import('./components/SobreCerty'));
const TrustPage = lazy(() => import('./components/TrustPage'));
const Blog = lazy(() => import('./components/Blog'));
const BlogPost = lazy(() => import('./components/BlogPost'));
const BlogCategory = lazy(() => import('./components/BlogCategory'));
const ProductSolutionPage = lazy(() => import('./components/ProductSolutionPage'));
const ProductFeaturePage = lazy(() => import('./components/ProductFeaturePage'));
const SolutionsHub = lazy(() => import('./components/SolutionsHub'));
const SolutionPage = lazy(() => import('./components/SolutionPage'));
const GuiasHub = lazy(() => import('./components/GuiasHub'));
const GuiaDetalle = lazy(() => import('./components/GuiaDetalle'));
const PortalesHub = lazy(() => import('./components/PortalesHub'));
const PortalDetalle = lazy(() => import('./components/PortalDetalle'));
const HerramientasHub = lazy(() => import('./components/HerramientasHub'));
const HerramientaCaducidad = lazy(() => import('./components/HerramientaCaducidad'));
const HerramientaChecklistInstalacion = lazy(() => import('./components/HerramientaChecklistInstalacion'));
const HerramientaDiagnostico = lazy(() => import('./components/HerramientaDiagnostico'));
const HerramientaDirectorioPortales = lazy(() => import('./components/HerramientaDirectorioPortales'));
const HerramientaComparadorAcceso = lazy(() => import('./components/HerramientaComparadorAcceso'));
const HerramientaPlanificadorRenovacion = lazy(() => import('./components/HerramientaPlanificadorRenovacion'));
const HerramientaImpactoIncidencias = lazy(() => import('./components/HerramientaImpactoIncidencias'));
const HerramientaInspectorCertificado = lazy(() => import('./components/HerramientaInspectorCertificado'));
const HerramientaComprobarFirmaPdf = lazy(() => import('./components/HerramientaComprobarFirmaPdf'));
const HerramientaElegirCertificado = lazy(() => import('./components/HerramientaElegirCertificado'));
const PlantillasHub = lazy(() => import('./components/PlantillasHub'));
const PlantillaDetalle = lazy(() => import('./components/PlantillaDetalle'));
const GlosarioHub = lazy(() => import('./components/GlosarioHub'));
const GlosarioDetalle = lazy(() => import('./components/GlosarioDetalle'));
const NotFound = lazy(() => import('./components/NotFound'));
const ErrorDirectory = lazy(() => import('./components/ErrorDirectory'));

function App() {
  return (
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <Suspense fallback={<div className="min-h-[60vh] bg-white" aria-label="Cargando contenido" />}>
        <Routes>
        <Route element={<SiteLayout />}>
          <Route path="/" element={<Home />} />
          <Route path="/success" element={<Success />} />
          <Route path="/payment-failed" element={<PaymentFailed />} />
          <Route path="/downloads" element={<Downloads />} />
          <Route path="/docs" element={<Documentation />} />
          <Route path="/docs/:sectionSlug" element={<DocsSectionPage />} />
          <Route path="/sobre-certy" element={<SobreCerty />} />
          <Route path="/privacidad" element={<TrustPage variant="privacidad" />} />
          <Route path="/aviso-legal" element={<TrustPage variant="aviso-legal" />} />
          <Route path="/404" element={<NotFound />} />

          <Route path="/blog" element={<Blog />} />
          <Route path="/recursos/errores" element={<ErrorDirectory />} />
          <Route path="/blog/categoria/:categorySlug" element={<BlogCategory />} />
          <Route path="/blog/:slug" element={<BlogPost />} />

          <Route path="/guias" element={<GuiasHub />} />
          <Route path="/guias/:slug" element={<GuiaDetalle />} />

          <Route path="/portales" element={<PortalesHub />} />
          <Route path="/portales/:slug" element={<PortalDetalle />} />

          <Route path="/herramientas" element={<HerramientasHub />} />
          <Route path="/soluciones" element={<SolutionsHub />} />
          <Route path="/software-gestion-certificados-digitales" element={<ProductSolutionPage solutionSlug="software" />} />
          <Route
            path="/buscar-certificados-digitales-en-windows"
            element={<ProductSolutionPage solutionSlug="buscar" />}
          />
          <Route
            path="/descargar-gestor-certificados-digitales"
            element={<ProductSolutionPage solutionSlug="descargar" />}
          />
          <Route
            path="/control-caducidad-certificados-digitales"
            element={<ProductSolutionPage solutionSlug="caducidad" />}
          />
          <Route
            path="/acceso-a-portales-oficiales-con-certificado-digital"
            element={<ProductSolutionPage solutionSlug="portales" />}
          />
          <Route
            path="/gestionar-varios-certificados-digitales"
            element={<ProductSolutionPage solutionSlug="varios-certificados" />}
          />
          <Route path="/navegador-varios-certificados-digitales" element={<ProductFeaturePage feature="tabs" />} />
          <Route path="/importar-certificados-pfx-p12-masivamente" element={<ProductFeaturePage feature="import" />} />
          <Route path="/firma-pdf-con-certificado-digital" element={<ProductFeaturePage feature="pdf" />} />
          <Route path="/para/gestorias" element={<SolutionPage solutionSlug="gestorias" />} />
          <Route path="/para/abogados" element={<SolutionPage solutionSlug="abogados" />} />
          <Route path="/para/asesorias-fiscales" element={<SolutionPage solutionSlug="asesorias-fiscales" />} />
          <Route path="/para/departamentos-laborales" element={<SolutionPage solutionSlug="departamentos-laborales" />} />
          <Route path="/para/administradores-de-fincas" element={<SolutionPage solutionSlug="administradores-de-fincas" />} />
          <Route path="/para/notarias" element={<SolutionPage solutionSlug="notarias" />} />
          <Route path="/para/pymes" element={<SolutionPage solutionSlug="pymes" />} />

          <Route path="/plantillas" element={<PlantillasHub />} />
          <Route path="/plantillas/:slug" element={<PlantillaDetalle />} />

          <Route path="/glosario" element={<GlosarioHub />} />
          <Route path="/glosario/:slug" element={<GlosarioDetalle />} />

          <Route
            path="/herramientas/calculadora-caducidad-certificado-digital"
            element={<HerramientaCaducidad />}
          />
          <Route
            path="/herramientas/checklist-instalacion-certificado-digital"
            element={<HerramientaChecklistInstalacion />}
          />
          <Route
            path="/herramientas/diagnostico-certificado-no-aparece"
            element={<HerramientaDiagnostico />}
          />
          <Route
            path="/herramientas/directorio-portales-oficiales-certificado-digital"
            element={<HerramientaDirectorioPortales />}
          />
          <Route
            path="/herramientas/certificado-vs-clave-vs-dnie"
            element={<HerramientaComparadorAcceso />}
          />
          <Route
            path="/herramientas/planificador-renovacion-certificados"
            element={<HerramientaPlanificadorRenovacion />}
          />
          <Route
            path="/herramientas/calculadora-impacto-incidencias-certificados"
            element={<HerramientaImpactoIncidencias />}
          />
          <Route path="/herramientas/inspector-certificado-digital" element={<HerramientaInspectorCertificado />} />
          <Route path="/herramientas/comprobar-firma-pdf" element={<HerramientaComprobarFirmaPdf />} />
          <Route path="/herramientas/que-certificado-digital-necesito" element={<HerramientaElegirCertificado />} />

          <Route path="*" element={<NotFound />} />
        </Route>
        </Routes>
      </Suspense>
    </Router>
  );
}

export default App;
